import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import ReactTable from "components/lib/Global/ReactTable";
import { getMaintainYourCompanyReviewStatus } from "config/documentData";
import { getEmployeeAsync } from "state/slices/employee";
import CommentsPopup from "components/lib/Global/CommentsPopup";
import { PencilIcon, XIcon, CheckIcon } from "@heroicons/react/outline";
import SpinnerIcon from "components/lib/Shared/Icons/SpinnerIcon";
import { addComment, editComment, getComments } from "state/slices/comment";
import { resolveFields, invalidateFields } from "state/slices/resolveField";
import _get from "lodash/get";
import CommentsList from "components/lib/Global/CommentsList";

const TypeOfChange = ({ isViewMode, transactions }) => {
  const { transactionUnderReview } = useSelector(
    (state) => state.transactionSlice
  );
  const { company } = useSelector((state) => state.companyDashboardSlice);
  const { comments } = useSelector((state) => state.commentsSlice);
  const typeOfChangeComments = comments.filter(
    (comment) => comment.formName === "typeOfChange"
  );

  const dispatch = useDispatch();
  const params = useParams();
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedRow, setSelectedRow] = useState({});
  useEffect(() => {
    if (params.companyId) {
      dispatch(getEmployeeAsync({ companyId: params.companyId }));
    }
  }, [dispatch, params.companyId]);

  const handleSaveComment = async (data, row) => {
    if (data?.commentId) {
      await dispatch(editComment({ commentId: data.commentId, data }));
    } else {
      await dispatch(
        addComment({
          data: {
            ...data,
            company: company._id,
            transaction: row.original._id,
          },
        })
      );
    }
  };

  // ... (keep existing resolve/invalidate field handlers unchanged)
  const handleResolveBtnClicked = async (row) => {
    setIsLoading(true);
    setSelectedRow(row);
    const { original } = row;
    const commentsToBeResolved = comments?.filter(
      (comment) =>
        comment.formName === "typeOfChange" &&
        comment.transaction === original?._id &&
        !comment.isResolved
    );
    const commentsToBeResolvedIds =
      commentsToBeResolved?.map(({ _id }) => _id) ?? [];
    const sliceIndex = transactions.findIndex(
      (trx) => trx._id === original._id
    );
    const promise = await dispatch(
      resolveFields({
        data: {
          formId: _get(original, "_id"),
          formName: "initiateTransactionChange",
          fieldName: original.changeType,
          resolvedFields: [original.changeType],
          commentsToBeResolvedIds,
        },
        updateEffectKey: "updateTransactionUnderReview",
        updateEffectPath: `transactionSlice.transactionUnderReview.items.transactions.${sliceIndex}`,
      })
    );
    if (commentsToBeResolvedIds.length > 0) {
      const query = { companyId: commentsToBeResolved[0]?.company };
      // if (isReviewerMode) query.createdBy = currentUser?._id;
      await dispatch(getComments(query));
    }

    setIsLoading(false);
    setSelectedRow({});
  };

  const handleInvalidateBtnClicked = async (row) => {
    setIsLoading(true);
    setSelectedRow(row);
    const { original } = row;
    const sliceIndex = transactions.findIndex(
      (trx) => trx._id === original._id
    );
    const commentsToBeResolved = comments?.filter(
      (comment) =>
        comment.formName === "typeOfChange" &&
        comment.transaction === original?._id &&
        !comment.isResolved
    );
    const commentsToBeResolvedIds =
      commentsToBeResolved?.map(({ _id }) => _id) ?? [];
    const promise = await dispatch(
      invalidateFields({
        data: {
          formId: _get(original, "_id"),
          formName: "initiateTransactionChange",
          fieldName: original.changeType,
          invalidFields: [original.changeType],
          commentsToBeResolvedIds,
        },
        updateEffectKey: "updateTransactionUnderReview",
        updateEffectPath: `transactionSlice.transactionUnderReview.items.transactions.${sliceIndex}`,
      })
    );
    setIsLoading(false);
    setSelectedRow({});
  };

  const columns = [
    {
      Header: "Batch No.",
      accessor: "BatchNo",
      Cell: ({ row }) => row?.original?.batchNo || "-",
    },
    {
      Header: "Application Type",
      accessor: "applicationType",

      Cell: ({ row }) => {
        return row.original?.applicationType || "Incorporation";
      },
    },
    {
      Header: "Change Type",
      accessor: "changeType",
      Cell: ({ row: { original } }) => {
        const capitalize = (str) => {
          if (!str) return null;
          return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
        };

        return capitalize(original?.changeType) || original?.type || "-";
      },
    },
    {
      Header: "Transaction Type",
      accessor: "transactionType",
      Cell: ({ row: { original } }) => {
        const capitalize = (str) => {
          if (!str) return null;
          let transactionType =
            str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
          return transactionType;
        };

        return capitalize(original?.transactionType) || "Automatic";
      },
    },
    {
      Header: "Tnx No.",
      accessor: "TnxNo",
      Cell: ({ row: { original } }) => {
        return original?.trxRef || original?.registrationNumber || "-";
      },
    },
  ];

  if (!isViewMode) {
    columns.push({
      Header: "Actions",
      Cell: ({ row }) => {
        const { original } = row;
        const { resolvedFields } = original;
        return (
          <div className="flex items-center space-x-2">
            {!resolvedFields.includes(original?.changeType) && (
              <CommentsPopup
                handleSaveComment={(data) => {
                  handleSaveComment(data, row);
                }}
                formName="typeOfChange"
                fieldName={original.applicationType}
                fieldLabel={original.changeType}
                comment={{}}
              >
                <span>Review</span>
                <PencilIcon className="inline w-4 h-4" />
              </CommentsPopup>
            )}
            <div className={`flex items-center justify-center`}>
              {resolvedFields.includes(original?.changeType) ? (
                <button
                  className={`flex space-x-1 ${
                    isViewMode ? "cursor-not-allowed" : "cursor-pointer"
                  }`}
                  onClick={() =>
                    isViewMode ? null : handleInvalidateBtnClicked(row)
                  }
                >
                  {isLoading && selectedRow.index === row.index ? (
                    <SpinnerIcon className="text-primary" />
                  ) : (
                    <>
                      <span>Accepted </span>
                      <XIcon className="inline w-4 h-4" />
                    </>
                  )}
                </button>
              ) : (
                <button
                  className={`flex space-x-1 ${
                    isViewMode ? "cursor-not-allowed" : "cursor-pointer"
                  }`}
                  onClick={() =>
                    isViewMode ? null : handleResolveBtnClicked(row)
                  }
                >
                  {isLoading && selectedRow.index === row.index ? (
                    <SpinnerIcon className="text-primary" />
                  ) : (
                    <>
                      <span>Accept</span>
                      <CheckIcon className="inline w-4 h-4" />
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        );
      },
    });
  }

  return (
    <>
      {typeOfChangeComments.length > 0 && (
        <div className="px-8 pt-5 pb-6 mb-4 bg-white">
          <CommentsList
            comments={typeOfChangeComments}
            toggleCommentStatus={!isViewMode}
          />
          <p className="mt-6 text-sm text-primary">
            Comments: {typeOfChangeComments.length} of{" "}
            {typeOfChangeComments.length}
          </p>
        </div>
      )}
      {transactions?.length > 0 && (
        <div className="mt-6 w-full bg-white shadow overflow-x-auto">
          <ReactTable
            columns={columns}
            data={transactions || []}
            dataRowCellStyle="pl-4 leading-snug py-2"
            // renderRowSubComponent={renderRowSubComponent}
            paginate={true}
            totalRows={10}
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
          />
        </div>
      )}
    </>
  );
};

export default TypeOfChange;
